import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import getBaseQueryWithReauth from '../../../utils/api/query-auth'
import getAuth from '../../../utils/api/getToken'

const REFRESH_TIMEOUT = 15 // 15 seconds

const baseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}/Timesheet`,
	prepareHeaders: headers => {
		const token = getAuth()
		if (token) {
			headers.set('authorization', `Bearer ${token.auth}`)
		}
		return headers
	},
})

export interface MessageTemplateApiResponse {	
	Id?: number | string;
	id?: number | string;	
	Message?: string;
	message?: string;
	Title?: string;
	title?: string;
}

export interface ResponseMessageDto<T> {
	Data?: T;
	data?: T;
	Message?: string;
	message?: string;
	Path?: string | null;
	path?: string | null;
	Status?: string;
	status?: string;
}

export interface TimesheetNotificationTemplate {
	id: string;
	message: string;
	title: string;
}

export type TimesheetNotificationContentType = 'custom' | 'template';

type MessageTemplatesResponse =
	| ResponseMessageDto<MessageTemplateApiResponse[]>
	| MessageTemplateApiResponse[]
	| Record<string, unknown>


export const timesheetMessageTemplatesApi = createApi({
	reducerPath: 'timesheetMessageTemplates',
	baseQuery: getBaseQueryWithReauth(baseQuery),
	tagTypes: ['MessageTemplates'],
	keepUnusedDataFor: 0,
	endpoints: builder => ({
		getMessageTemplates: builder.query<TimesheetNotificationTemplate[], void>({
			query: () => ({
				url: `/getMessageTemplates`,
				responseHandler: async response =>
					getMessageTemplateItems(await response.json()),
			}),
			transformResponse: (response: MessageTemplateApiResponse[]) =>
				response
					.map(normalizeMessageTemplate)
					.filter(template => template.id),
			providesTags: ['MessageTemplates'],
			keepUnusedDataFor: REFRESH_TIMEOUT,
		}),
	}),
})

export const { useGetMessageTemplatesQuery } = timesheetMessageTemplatesApi


const getMessageTemplateItems = (
	response: MessageTemplatesResponse,
): MessageTemplateApiResponse[] => {
	if (Array.isArray(response)) {
		return response
	}

	if (!isRecord(response)) {
		return []
	}

	const candidates = [
		response['Data'],
		response['data'],
		response['Result'],
		response['result'],
		response['Value'],
		response['value'],
		response['items'],
		response['itemList'],
	]

	for (const candidate of candidates) {
		if (Array.isArray(candidate)) {
			return candidate as MessageTemplateApiResponse[]
		}

		if (isRecord(candidate)) {
			const nestedItems = getMessageTemplateItems(candidate)
			if (nestedItems.length > 0) {
				return nestedItems
			}
		}
	}

	return []
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeMessageTemplate = (template: MessageTemplateApiResponse): TimesheetNotificationTemplate => {
	const id = template.Id ?? template.id
	const normalizedId = id === undefined ? '' : String(id)

	return {
		id: normalizedId,
		message: template.Message ?? template.message ?? '',
		title: template.Title ?? template.title ?? `Template ${normalizedId}`,
	}
}
